/**
 * Gen-3 stat computation. v0.1 assumes EV=0 across the board (Premise 4
 * of the Pokédex design doc deferred EV tracking).
 *
 * HP   = floor((2 × base + IV) × level / 100) + level + 10
 * Other = floor((floor((2 × base + IV) × level / 100) + 5) × natureMod)
 *
 * `natureMod` = 1.1 when the nature's `plus` stat matches; 0.9 when the
 * `minus` stat matches; 1.0 otherwise (neutral or unrelated stat). HP is
 * never natured in Gen 3, so the formula skips the natureMod.
 *
 * Used by PokemonFullSheet to render the in-battle numeric next to each
 * IV bar so the player can read "23 HP" / "11 Atk" alongside the
 * 0-31 IV value.
 */

import { NATURE_MODS, type Nature, type StatKey } from './natures';

export type BaseStats = {
  hp: number;
  atk: number;
  def: number;
  spa: number;
  spd: number;
  spe: number;
};

export type IvKey = 'hp' | StatKey;

/** Compute one stat at a given level with the given IV + nature. */
export function computeStat(
  stat: IvKey,
  base: number,
  iv: number,
  level: number,
  nature: Nature,
): number {
  if (stat === 'hp') {
    return Math.floor(((2 * base + iv) * level) / 100) + level + 10;
  }
  const raw = Math.floor(((2 * base + iv) * level) / 100) + 5;
  const mod = NATURE_MODS[nature];
  let natureMod = 1;
  if (mod.plus === stat) natureMod = 1.1;
  else if (mod.minus === stat) natureMod = 0.9;
  // floor after the natureMod multiplication — cartridge floors here.
  return Math.floor(raw * natureMod);
}
